import { ScrollText } from "lucide-react";
import { DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import { Card } from "@/components/ui/card";

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: GuildConfig["logging"]) => void;
};

type LogCfg = GuildConfig["logging"];

// Every individual toggle below can send to its own channel. Leave its picker on
// "(category default)" to fall back to the category channel, and leave that empty
// to fall back to the single "Default log channel" at the top.
function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  channels,
  overrideValue,
  onOverrideChange,
  fallbackLabel,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  channels: DiscordChannel[];
  overrideValue?: string;
  onOverrideChange?: (v: string) => void;
  fallbackLabel?: string;
}) {
  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">{label}</div>
          {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        </div>
        <Switch checked={checked} onCheckedChange={onChange} />
      </div>
      {onOverrideChange && (
        <div className="flex items-center gap-2 pt-2 border-t border-border/40">
          <span className="text-[11px] font-medium text-muted-foreground shrink-0">Send to:</span>
          <div className="flex-1 min-w-0">
            <ChannelPicker
              channels={channels}
              value={overrideValue || ""}
              onChange={(v) => onOverrideChange(v === "__none__" ? "" : v)}
              placeholder={`(${fallbackLabel || "category default"})`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function LoggingTab({ config, channels, onChange }: Props) {
  const cfg = config.logging;
  const text = channels.filter((ch) => ch.type === 0);
  const channelOptions = channels.filter((ch) => ch.type === 0 || ch.type === 5);
  const update = <K extends keyof LogCfg>(k: K, v: LogCfg[K]) =>
    onChange({ ...cfg, [k]: v });

  const channelField = (
    key:
      | "message_channel_id"
      | "member_channel_id"
      | "moderation_channel_id"
      | "server_channel_id"
      | "voice_channel_id",
    label: string,
  ) => (
    <Field label={label} hint="Empty = use default log channel. Falls back further if a toggle below has its own channel unset.">
      <ChannelPicker
        channels={text}
        value={cfg[key]}
        onChange={(v) => update(key, v === "__none__" ? "" : v)}
      />
    </Field>
  );

  // Wires a toggle to its own optional channel override (key + "_channel_id"),
  // so every single log event can be individually routed to any channel.
  const toggleRow = (
    key: keyof LogCfg,
    channelKey: keyof LogCfg,
    label: string,
    fallbackLabel: string,
    hint?: string,
  ) => (
    <ToggleRow
      label={label}
      hint={hint}
      checked={Boolean(cfg[key])}
      onChange={(v) => update(key, v as LogCfg[typeof key])}
      channels={text}
      overrideValue={(cfg[channelKey] as string) || ""}
      onOverrideChange={(v) => update(channelKey, v as LogCfg[typeof channelKey])}
      fallbackLabel={fallbackLabel}
    />
  );

  return (
    <Section
      icon={ScrollText}
      title="Logging Center"
      description="Central audit logs for messages, members, moderation, server changes and voice activity."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No slash commands.</strong> When logging is enabled, the bot posts events to your chosen channels as
          members send/edit/delete messages, join/leave, get banned/unbanned, or when channels/roles are
          created/deleted/edited—according to the toggles below. Every toggle has its own <strong>Send to</strong>{" "}
          channel picker — leave it empty to fall back to its category channel, then to the default log channel.
          Use <strong>Ignored channels</strong> to cut noise.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Default log channel" hint="Final fallback when a category and toggle channel are both empty.">
          <ChannelPicker
            channels={text}
            value={cfg.default_channel_id}
            onChange={(v) => update("default_channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
        <Field label="Ignored channels/categories" hint="Message edit/delete logs are skipped here.">
          <MultiSelect
            options={channelOptions}
            value={cfg.ignored_channel_ids}
            onChange={(v) => update("ignored_channel_ids", v)}
            prefix="#"
            placeholder="+ add ignored channel"
          />
        </Field>
      </div>

      <ToggleRow
        label="Ignore bot messages"
        hint="Recommended on, prevents bot/webhook noise in message logs."
        checked={cfg.ignore_bot_messages}
        onChange={(v) => update("ignore_bot_messages", v)}
        channels={text}
      />

      <div className="grid xl:grid-cols-2 gap-4">
        <Card className="p-4 space-y-4 border-border/50">
          <div>
            <div className="font-medium">Messages</div>
            <div className="text-xs text-muted-foreground">Deleted and edited messages.</div>
          </div>
          {channelField("message_channel_id", "Message log channel (category default)")}
          {toggleRow("log_message_delete", "log_message_delete_channel_id", "Message deleted", "Message log channel")}
          {toggleRow("log_message_edit", "log_message_edit_channel_id", "Message edited", "Message log channel")}
        </Card>

        <Card className="p-4 space-y-4 border-border/50">
          <div>
            <div className="font-medium">Members</div>
            <div className="text-xs text-muted-foreground">Join, leave, nickname and role changes.</div>
          </div>
          {channelField("member_channel_id", "Member log channel (category default)")}
          {toggleRow("log_member_join", "log_member_join_channel_id", "Member joined", "Member log channel")}
          {toggleRow("log_member_leave", "log_member_leave_channel_id", "Member left", "Member log channel")}
          {toggleRow("log_member_update", "log_member_update_channel_id", "Member updated", "Member log channel")}
        </Card>

        <Card className="p-4 space-y-4 border-border/50">
          <div>
            <div className="font-medium">Moderation</div>
            <div className="text-xs text-muted-foreground">Bans and unbans.</div>
          </div>
          {channelField("moderation_channel_id", "Moderation log channel (category default)")}
          {toggleRow("log_ban", "log_ban_channel_id", "Member banned", "Moderation log channel")}
          {toggleRow("log_unban", "log_unban_channel_id", "Member unbanned", "Moderation log channel")}
        </Card>

        <Card className="p-4 space-y-4 border-border/50">
          <div>
            <div className="font-medium">Server changes</div>
            <div className="text-xs text-muted-foreground">Roles and channels created, deleted or edited.</div>
          </div>
          {channelField("server_channel_id", "Server log channel (category default)")}
          {toggleRow("log_role_create", "log_role_create_channel_id", "Role created", "Server log channel")}
          {toggleRow("log_role_delete", "log_role_delete_channel_id", "Role deleted", "Server log channel")}
          {toggleRow("log_role_update", "log_role_update_channel_id", "Role updated", "Server log channel")}
          {toggleRow("log_channel_create", "log_channel_create_channel_id", "Channel created", "Server log channel")}
          {toggleRow("log_channel_delete", "log_channel_delete_channel_id", "Channel deleted", "Server log channel")}
          {toggleRow("log_channel_update", "log_channel_update_channel_id", "Channel updated", "Server log channel")}
        </Card>

        <Card className="p-4 space-y-4 border-border/50 xl:col-span-2">
          <div>
            <div className="font-medium">Voice</div>
            <div className="text-xs text-muted-foreground">Voice channel join, leave and move events.</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {channelField("voice_channel_id", "Voice log channel")}
            <ToggleRow
              label="Voice activity"
              checked={cfg.log_voice}
              onChange={(v) => update("log_voice", v)}
              channels={text}
            />
          </div>
        </Card>
      </div>
    </Section>
  );
}
